/* Overview — the leadership tab. Answers, in order: how much of our shipping runs
   on our own integrations, is the volume growing, and is anything broken.

   Everything above the fold comes from ONE call (/health/overview) so the KPIs can
   never disagree with each other; the charts below fetch separately. */
import React, {useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, CircularProgress, Stack, Typography} from '@mui/material'
import {Bar, CartesianGrid, Cell, ComposedChart, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts'
import {LoadErrorPanel, TableSkeleton} from '../../components'
import {
  usePcisHealthBreakdownQuery,
  usePcisHealthLatencyQuery,
  usePcisHealthOverviewQuery,
  usePcisRoutingSplitQuery,
} from '../../../../redux/supplyApiSlice'
import {
  AMBER,
  Callout,
  CORAL,
  Delta,
  Empty,
  INDIGO,
  Panel,
  SplitLabelledBar,
  Stat,
  StatGrid,
  TEAL,
  VIOLET,
  compact,
  fmtBucket,
  money,
  ms,
  nf,
  pct,
} from './parts'

/* Thresholds that turn a number into a callout. Deliberately explicit rather than
   "top 3 worst" — a fleet where everything is healthy should produce an empty
   attention list, not three items dressed up as problems. */
const ATTENTION = {
  failureRatePct: 3, // a courier failing >3% of placements
  p95Ms: 3000, // …or taking >3s at p95
  minVolume: 50, // ignore couriers too small for the rate to mean anything
}

/* Chart tooltip — recharts' default is unstyled and shows raw dataKeys. */
function ChartTip({active, payload, label, bucket}) {
  if (!active || !payload || !payload.length) return null
  const p = payload[0].payload
  return (
    <Box sx={{bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: '10px', px: 1.5, py: 1.15, boxShadow: '0 8px 24px rgba(15,23,42,.12)'}}>
      <Typography sx={{fontSize: 11.5, fontWeight: 700, mb: 0.5}}>{fmtBucket(label, bucket)}</Typography>
      <Stack spacing={0.25}>
        <Row swatch={TEAL.main} label='Direct Integ.' value={nf(p.di)} />
        {/* Guarded on null rather than falsy: 0 is a real answer (that bucket routed entirely
            through DI) and must still render, while a genuinely absent figure must not read as
            zero. Both series now come from one aggregation, so absence is rare — but the
            distinction is the difference between "no Clickpost" and "don't know". */}
        {p.clickpost == null ? null : <Row swatch={AMBER.main} label='Clickpost' value={nf(p.clickpost)} />}
        {p.diSharePct == null ? null : <Row swatch={INDIGO.main} label='DI share' value={pct(p.diSharePct, 1)} />}
        {/* No "Failed" row: this chart stopped plotting failures when it became the routing split,
            and a figure in the tooltip with no corresponding mark on the chart reads as one of the
            series. Placement failures live on the Failures tab. */}
      </Stack>
    </Box>
  )
}
ChartTip.propTypes = {active: PropTypes.bool, payload: PropTypes.array, label: PropTypes.any, bucket: PropTypes.string}

function Row({swatch, label, value}) {
  return (
    <Stack direction='row' alignItems='center' spacing={1}>
      <Box sx={{width: 8, height: 8, borderRadius: '2px', bgcolor: swatch}} />
      <Typography sx={{fontSize: 11.5, color: 'text.secondary', minWidth: 78}}>{label}</Typography>
      <Typography sx={{fontSize: 11.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>{value}</Typography>
    </Stack>
  )
}
Row.propTypes = {swatch: PropTypes.string, label: PropTypes.node, value: PropTypes.node}

/* Shown on cards that keep displaying the previous date range's numbers while the new ones load.
   Without it the stale figure is indistinguishable from the current one — which is what made the
   volume cards look stuck on a 30-day range, where the Proship count takes several seconds. */
function Refreshing() {
  return (
    <Stack direction='row' alignItems='center' spacing={0.75} sx={{flexShrink: 0, color: 'text.disabled'}}>
      <CircularProgress size={11} thickness={5} sx={{color: 'text.disabled'}} />
      <Typography sx={{fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap'}}>Updating…</Typography>
    </Stack>
  )
}

export default function TabOverview({filters, bucket, pollMs}) {
  const poll = pollMs ? {pollingInterval: pollMs} : undefined

  const {data: ov, isFetching, isError, error, refetch} = usePcisHealthOverviewQuery(filters, poll)
  /* PCIS's timeseries is no longer fetched here: the volume chart is now driven entirely by the
     routing split, which carries both series and its own bucket labels. Keeping it would mean two
     sources for one chart and a join between them on label strings. */
  const {data: byCourier} = usePcisHealthBreakdownQuery({...filters, dimension: 'COURIER', limit: 12}, poll)
  const {data: latency} = usePcisHealthLatencyQuery({...filters, limit: 25}, poll)

  /* DI vs Clickpost volume, from Proship's own orders via /health/routing-split. Both sides come
     from one aggregation over the same population — successful B2C placements windowed on
     awbRegisteredDate — rather than the old approach of reading PCIS's native count and
     subtracting it from a separate Proship total, which could only ever produce a fleet-wide
     number and needed a count call per chart bucket on top.

     Point-in-time, unlike ov.nativeSharePct and ov.accountsNative (an account COUNT, not volume):
     each order keeps the routing actually applied at booking, so these don't shift when an
     account's config changes later. */
  /* Bucket granularity for the routing split. Derived from the window rather than read off the
     timeseries response, so the query doesn't have to wait for PCIS first — and it matches what
     PCIS picks anyway (hourly for a day or less, daily beyond). The backend caps HOUR at 7 days. */
  const splitBucket = useMemo(() => {
    const hours = (new Date(filters.to) - new Date(filters.from)) / 3600000
    /* The Trend picker wins when the user sets it, with two caps the backend enforces anyway:
       HOUR is rejected beyond 7 days, and WEEK isn't a granularity this endpoint offers — both
       fall back to DAY rather than erroring. '' is the picker's Auto. */
    if (bucket === 'HOUR' && hours <= 24 * 7) return 'HOUR'
    if (bucket === 'HOUR' || bucket === 'DAY' || bucket === 'WEEK') return 'DAY'
    return hours > 0 && hours <= 48 ? 'HOUR' : 'DAY'
  }, [filters.from, filters.to, bucket])

  /* currentData, NOT data: RTK Query's `data` returns the last result for the endpoint regardless
     of args, so on a date-window change it keeps serving the PREVIOUS window's value until the new
     fetch lands — which is how this once briefly read 100%. currentData is undefined until THIS
     window resolves.

     One call now replaces three (PCIS native volume + Proship whole-window count + a per-bucket
     count per chart bucket). Everything routing-related below reads from it, so the KPI cards, the
     donut, the trend and the per-courier split are guaranteed to agree — they are literally the
     same aggregation rolled up different ways. */
  const {currentData: split, isFetching: routingRefreshing, error: splitErr} = usePcisRoutingSplitQuery(
    {from: filters.from, to: filters.to, bucket: splitBucket},
    poll
  )

  /* The endpoint rejects a window wider than 92 days (and hourly buckets beyond 7) with a 400 that
     explains itself. Without surfacing it, every routing card fell back to its empty state and read
     "No orders in this window" — telling the user their data is missing when the real answer is
     that they asked for too much. Distinguished from a genuine outage so the copy can say which. */
  const splitError = useMemo(() => {
    if (!splitErr) return null
    const msg = splitErr.message
    /* A 400 is the endpoint rejecting the request, and its message is already written for a
       human ("Requested range of 120 days exceeds the 92-day maximum… Narrow the date range"),
       so it goes through as-is. Anything else is a fault rather than a bad ask, and the raw
       message is developer-facing — hence the plain-English lead-in. */
    if (splitErr.status === 400 && msg) return msg
    return `Routing split unavailable${msg ? ` — ${msg}` : '.'}`
  }, [splitErr])

  /* The courier filter carries a PCIS courier_code (DELHIVERY, XPRESSBEES_REVERSE); the split is
     keyed by Proship's courierDetail.parent (Delhivery, XpressBees). Same brand, different
     spelling and different case, and reverse variants carry a suffix on one side only — so match
     on a normalised form rather than assuming the two systems agree. */
  const normCourier = (s) =>
    String(s || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .replace(/(RETURN|REVERSE|RVP)$/, '')

  /* The selected courier's row in the split, or null when nothing is selected. `undefined` means
     a courier IS selected but no row matched — a different case from "not selected", and handled
     separately below so a failed match degrades to the old DI-only view instead of showing zero. */
  const selectedCourierRow = useMemo(() => {
    if (!filters.courier || !split) return null
    const want = normCourier(filters.courier)
    return (split.byCourier || []).find((c) => normCourier(c.courierName) === want)
  }, [filters.courier, split])

  /* True when the courier filter is on but we couldn't line it up with a row in the split. Rather
     than render 0, the routing cards fall back to the PCIS-scoped DI figure and say the Clickpost
     side is unavailable — the same behaviour as before this endpoint existed. */
  const courierUnmatched = Boolean(filters.courier) && Boolean(split) && !selectedCourierRow

  /* Holds the last result so the cards keep showing the previous window's numbers while the new
     ones load, instead of dropping to an empty state on every switch. Briefly stale, never
     internally inconsistent — and now sourced from a single aggregation, so the two halves cannot
     come from different windows at all. */
  const lastRouting = useRef(null)

  const routing = useMemo(() => {
    if (!split) return lastRouting.current

    // Scoped to the selected courier when one matched, otherwise fleet-wide.
    const src = filters.courier ? selectedCourierRow : split
    if (!src) return filters.courier ? null : lastRouting.current

    const nativeOrders = Number(src.nativeOrders) || 0
    const clickpostOrders = Number(src.clickpostOrders) || 0
    const totalOrders = Number(src.totalOrders) || nativeOrders + clickpostOrders
    if (!totalOrders) {
      if (!filters.courier) lastRouting.current = null
      return null
    }
    const nativeSharePct = Math.min(100, (nativeOrders / totalOrders) * 100)
    const next = {
      totalOrders,
      nativeOrders,
      nativeSharePct,
      clickpostOrders,
      clickpostSharePct: 100 - nativeSharePct,
    }
    // Only the fleet-wide figure is worth holding across windows; a courier-scoped one is not.
    if (!filters.courier) lastRouting.current = next
    return next
  }, [split, filters.courier, selectedCourierRow])

  /* Successful placements in the CURRENT filter scope, straight off /health/overview — which
     honours courier, account, merchant and the rest. PCIS only ever sees natively-routed orders,
     so this is DI volume for whatever is selected. Used where the Proship-derived split can't be
     narrowed to the selection. */
  const courierScopedDi = Math.max(0, (Number(ov?.totalOrders) || 0) - (Number(ov?.failedOrders) || 0))

  /* ── DI vs Clickpost, per chart bucket ────────────────────────────────────────────────────
     Both series now come from the same response, so they cannot disagree and no bucket can be
     missing its Clickpost half. This replaces a per-bucket fan-out of count calls — one HTTP call
     per chart bucket, all-or-nothing — that silently blanked the whole Clickpost series whenever
     any single call failed.

     Under a courier filter the per-courier series ships in the same payload (courierBuckets), so
     the filtered trend costs no extra request. */
  const trendSplitAvailable = !courierUnmatched
  const trend = useMemo(() => {
    if (!split) return []
    const rows = filters.courier
      ? (split.courierBuckets || []).find((c) => normCourier(c.courierName) === normCourier(filters.courier))?.buckets || []
      : split.buckets || []
    return rows.map((b2) => {
      const di = Number(b2.nativeOrders) || 0
      const clickpost = Number(b2.clickpostOrders) || 0
      const total = Number(b2.totalOrders) || di + clickpost
      return {
        label: b2.label,
        di,
        clickpost,
        diSharePct: total ? Math.min(100, (di / total) * 100) : null,
      }
    })
  }, [split, filters.courier])

  /* Kept so the chart's empty/partial states still have something to report, but with both series
     from one aggregation this is now always 0 unless a courier failed to match. */
  const missingBuckets = useMemo(() => trend.filter((p) => p.clickpost == null).length, [trend])

  /* Direct Integration vs Clickpost per parent courier — both sides, which is new. Until the
     covering index existed only the DI half could be sourced per courier: Clickpost lives solely
     in Proship's orders, and grouping those by courier was a ~190s full fetch. Now it arrives in
     the same response as everything else.

     Narrowed to the selected courier when one is chosen, so this panel follows the filter rail
     like the rest of the page. */
  const routingByCourier = useMemo(() => {
    const rows = (split?.byCourier || [])
      .map((c) => ({
        name: c.courierName,
        nativeOrders: Number(c.nativeOrders) || 0,
        clickpostOrders: Number(c.clickpostOrders) || 0,
        totalOrders: Number(c.totalOrders) || 0,
        nativeSharePct: Number(c.nativeSharePct) || 0,
      }))
      .filter((c) => c.totalOrders > 0)
    const scoped = filters.courier
      ? rows.filter((c) => normCourier(c.name) === normCourier(filters.courier))
      : rows
    return scoped.sort((a, b) => b.totalOrders - a.totalOrders)
  }, [split, filters.courier])

  const courierRows = useMemo(() => (byCourier?.rows || []).filter((r) => r.total > 0), [byCourier])
  const maxCourier = courierRows[0]?.total || 1

  /* Attention list: threshold breaches, worst first. Derived here rather than
     server-side so the thresholds stay visible next to the copy that explains
     them. */
  const attention = useMemo(() => {
    const out = []
    const latByCourier = new Map((latency?.rows || []).map((r) => [r.key, r]))

    courierRows
      .filter((r) => r.total >= ATTENTION.minVolume && r.failureRatePct >= ATTENTION.failureRatePct)
      .sort((a, b) => b.failed - a.failed)
      .slice(0, 3)
      .forEach((r) =>
        out.push({
          tone: CORAL,
          icon: '⚠',
          title: `${r.key} · ${pct(r.failureRatePct)} failure rate`,
          body: `${nf(r.failed)} of ${nf(r.total)} placements failed in this window.`,
        })
      )

    courierRows
      .filter((r) => r.total >= ATTENTION.minVolume && (latByCourier.get(r.key)?.p95Ms || 0) >= ATTENTION.p95Ms)
      .sort((a, b) => (latByCourier.get(b.key)?.p95Ms || 0) - (latByCourier.get(a.key)?.p95Ms || 0))
      .slice(0, 2)
      .forEach((r) =>
        out.push({
          tone: AMBER,
          icon: '◷',
          title: `${r.key} · p95 ${ms(latByCourier.get(r.key)?.p95Ms)}`,
          body: 'Placements are succeeding but slowly — worth checking before it becomes timeouts.',
        })
      )

    if (ov && ov.accountsTotal > ov.accountsNative) {
      const left = ov.accountsTotal - ov.accountsNative
      out.push({
        tone: TEAL,
        icon: '→',
        title: `${nf(left)} accounts still on Clickpost`,
        body: routing
          ? `${pct(routing.clickpostSharePct)} of this window's volume (${nf(routing.clickpostOrders)} orders). That's the remaining migration runway.`
          : "That's the remaining migration runway.",
      })
    }
    return out
  }, [courierRows, latency, ov, routing])

  if (isError) return <LoadErrorPanel message={error?.message || 'Could not load the overview.'} onRetry={refetch} />
  if (isFetching && !ov) return <TableSkeleton kpis={6} rows={4} />
  if (!ov) return <Empty />

  return (
    <>
      <StatGrid>
        <Stat
          label='Couriers live on PCIS'
          value={nf(ov.couriersLive)}
          unit={`/ ${nf(ov.couriersTotal)}`}
          palette={INDIGO}
          share={ov.couriersTotal ? (ov.couriersLive / ov.couriersTotal) * 100 : 0}
          sub='Have at least one native account'
        />
        <Stat
          label='Accounts routed native'
          value={nf(ov.accountsNative)}
          unit={`/ ${nf(ov.accountsTotal)}`}
          palette={VIOLET}
          share={ov.accountsTotal ? (ov.accountsNative / ov.accountsTotal) * 100 : 0}
          sub={`${pct(ov.accountsTotal ? (ov.accountsNative / ov.accountsTotal) * 100 : 0)} migrated`}
        />
        {/* Proship's own total, NOT ov.totalOrders - ov.failedOrders. PCIS only stores orders it
            routed natively, so its "total" is the DI count — showing that under a plain "Orders
            placed" label read as the whole fleet's volume when it was only the native share of it.
            The PCIS period delta is dropped for the same reason: it moves with the native count,
            not with this total. */}
        {/* Now genuinely narrows to the selected courier — `routing` is the courier's own row in
            the split when one is chosen. The only fallback left is courierUnmatched: the filter
            names a courier the split has no row for, where the honest answer is the PCIS-scoped
            DI figure and an explicit note, not a zero. */}
        <Stat
          label={courierUnmatched ? 'Placed via Direct Integration' : 'Orders placed'}
          value={courierUnmatched ? nf(courierScopedDi) : routing ? nf(routing.totalOrders) : '—'}
          palette={INDIGO}
          sub={
            splitError
              ? splitError
              : courierUnmatched
              ? `${filters.courier} — no routing data matched this courier`
              : routingRefreshing
              ? 'Updating for this date range…'
              : routing
              ? `${nf(routing.nativeOrders)} Direct Integration · ${nf(routing.clickpostOrders)} Clickpost`
              : 'No orders in this window'
          }
        />
        {/* Leads with the order count rather than the percentage — the donut below already carries
            the share as its centre label, so repeating it here said the same thing twice. */}
        <Stat
          label='Volume on PCIS'
          value={courierUnmatched ? nf(courierScopedDi) : routing ? nf(routing.nativeOrders) : '—'}
          unit={!courierUnmatched && routing ? `/ ${nf(routing.totalOrders)}` : undefined}
          palette={TEAL}
          share={courierUnmatched ? undefined : routing?.nativeSharePct}
          sub={
            splitError
              ? splitError
              : courierUnmatched
              ? 'Routing split unavailable for this courier'
              : routingRefreshing
              ? 'Updating for this date range…'
              : routing
              ? `${pct(routing.nativeSharePct)} placed via Direct Integration`
              : 'No orders in this window'
          }
        />
        <Stat
          label='p95 placement latency'
          value={ov.p95LatencyMs == null ? '—' : ms(ov.p95LatencyMs)}
          palette={AMBER}
          delta={ov.p95LatencyDeltaMs == null ? null : <Delta value={ov.p95LatencyDeltaMs} unit='ms' goodWhenUp={false} digits={0} />}
          sub={ov.p50LatencyMs == null ? 'No courier calls in window' : `p50 ${ms(ov.p50LatencyMs)}`}
        />
      </StatGrid>

      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: 'minmax(0, 1.6fr) minmax(0, 1fr)'}, gap: 2, mt: 2}}>
        <Panel
          title='Order volume — Direct Integration vs Clickpost'
          note={
            !trendSplitAvailable
              ? `${splitBucket.toLowerCase()} buckets · no routing data matched ${filters.courier}`
              : missingBuckets
              ? /* Said out loud rather than left to inference: without this, buckets with no
                   Clickpost figure look identical to buckets with no Clickpost volume. */
                `${splitBucket.toLowerCase()} buckets · Clickpost unavailable for ${missingBuckets} of ${trend.length} buckets`
              : `${splitBucket.toLowerCase()} buckets${filters.courier ? ` · ${filters.courier} only` : ''} · DI share of volume on the right axis`
          }
          action={routingRefreshing ? <Refreshing /> : null}
        >
          {trend.length ? (
            <ResponsiveContainer width='100%' height={280}>
              <ComposedChart data={trend} margin={{top: 8, right: 8, bottom: 0, left: -12}}>
                <CartesianGrid strokeDasharray='3 5' stroke='#E2E8F0' vertical={false} />
                <XAxis
                  dataKey='label'
                  tickFormatter={(v) => fmtBucket(v, splitBucket)}
                  fontSize={10.5}
                  tickLine={false}
                  axisLine={{stroke: '#E2E8F0'}}
                  minTickGap={18}
                />
                <YAxis yAxisId='count' fontSize={10.5} tickFormatter={compact} tickLine={false} axisLine={false} width={48} />
                {/* Pinned to 0–100 rather than auto-scaled: this is a share, and letting recharts
                    fit the domain to the observed range turns a 3-point wobble into a cliff. */}
                <YAxis
                  yAxisId='rate'
                  orientation='right'
                  domain={[0, 100]}
                  fontSize={10.5}
                  tickFormatter={(v) => `${v}%`}
                  tickLine={false}
                  axisLine={false}
                  width={40}
                />
                <Tooltip content={<ChartTip bucket={splitBucket} />} cursor={{fill: 'rgba(99,102,241,.06)'}} />
                {/* DI on the bottom of the stack so its trend reads against a flat baseline —
                    the question this chart answers is how the DI share is moving. */}
                <Bar yAxisId='count' dataKey='di' stackId='v' fill={TEAL.main} radius={[0, 0, 0, 0]} maxBarSize={38} />
                <Bar yAxisId='count' dataKey='clickpost' stackId='v' fill={AMBER.main} radius={[3, 3, 0, 0]} maxBarSize={38} />
                <Line
                  yAxisId='rate'
                  type='monotone'
                  dataKey='diSharePct'
                  stroke={INDIGO.main}
                  strokeWidth={2}
                  strokeDasharray='5 3'
                  dot={{r: 2.5, fill: '#fff', strokeWidth: 2}}
                  activeDot={{r: 4}}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : splitError ? (
            <Empty>{splitError}</Empty>
          ) : routingRefreshing ? (
            <TableSkeleton rows={5} />
          ) : (
            <Empty />
          )}
        </Panel>

        <Panel
          title='Where volume routes'
          note={
            courierUnmatched
              ? `No routing data matched ${filters.courier}`
              : filters.courier
                ? `Direct Integration vs Clickpost fallback — ${filters.courier} only`
                : 'Direct Integration vs Clickpost fallback, across all couriers'
          }
          action={routingRefreshing ? <Refreshing /> : null}
        >
          {/* This used to blank under a courier filter, because the Clickpost side could only be
              derived fleet-wide and showing it here would have read as that courier's mix. The
              split is now per courier, so the donut narrows with the rest of the page. */}
          {splitError ? (
            <Empty>{splitError}</Empty>
          ) : courierUnmatched ? (
            <Empty>No routing data matched this courier.</Empty>
          ) : routing ? (
            /* Dimmed while refreshing so the figures below — which are still the PREVIOUS date
               range until both legs return — can't be read as the current window's answer. */
            <Box sx={{opacity: routingRefreshing ? 0.45 : 1, transition: 'opacity .2s'}}>
              <Box sx={{position: 'relative', height: 176}}>
                <ResponsiveContainer width='100%' height='100%'>
                  <PieChart>
                    <Pie
                      data={[
                        {name: 'PCIS native', value: routing.nativeOrders, fill: TEAL.main},
                        {name: 'Clickpost fallback', value: routing.clickpostOrders, fill: AMBER.main},
                      ]}
                      dataKey='value'
                      innerRadius={54}
                      outerRadius={78}
                      paddingAngle={2}
                      stroke='none'
                    >
                      <Cell key='native' fill={TEAL.main} />
                      <Cell key='clickpost' fill={AMBER.main} />
                    </Pie>
                    <Tooltip formatter={(v, n) => [nf(v), n]} />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none'}}>
                  <Typography sx={{fontSize: 26, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1, color: TEAL.main}}>
                    {Number(routing.nativeSharePct).toFixed(1)}%
                  </Typography>
                  <Typography sx={{fontSize: 9.5, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: 'text.secondary', mt: 0.5}}>
                    On PCIS
                  </Typography>
                </Box>
              </Box>
              <Stack spacing={1.5} sx={{mt: 1.5}}>
                <Box>
                  <Stack direction='row' justifyContent='space-between' sx={{mb: 0.5}}>
                    <Typography sx={{fontSize: 12.5}}>PCIS native</Typography>
                    <Typography sx={{fontSize: 12.5, fontWeight: 700}}>{nf(routing.nativeOrders)}</Typography>
                  </Stack>
                  <Box sx={{height: 8, borderRadius: '999px', bgcolor: 'action.hover', overflow: 'hidden'}}>
                    <Box sx={{height: '100%', width: `${routing.nativeSharePct}%`, backgroundImage: `linear-gradient(90deg, ${TEAL.g1}, ${TEAL.g2})`}} />
                  </Box>
                </Box>
                <Box>
                  <Stack direction='row' justifyContent='space-between' sx={{mb: 0.5}}>
                    <Typography sx={{fontSize: 12.5}}>Clickpost fallback</Typography>
                    <Typography sx={{fontSize: 12.5, fontWeight: 700}}>{nf(routing.clickpostOrders)}</Typography>
                  </Stack>
                  <Box sx={{height: 8, borderRadius: '999px', bgcolor: 'action.hover', overflow: 'hidden'}}>
                    <Box sx={{height: '100%', width: `${routing.clickpostSharePct}%`, backgroundImage: `linear-gradient(90deg, ${AMBER.g1}, ${AMBER.g2})`}} />
                  </Box>
                </Box>
              </Stack>
            </Box>
          ) : routingRefreshing ? (
            <TableSkeleton rows={5} />
          ) : (
            <Empty>No orders in this window.</Empty>
          )}
        </Panel>
      </Box>

      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: 'minmax(0, 1.2fr) minmax(0, 1fr)'}, gap: 2, mt: 2}}>
        {/* "Placement attempts" and "of DI volume" are both deliberate. These rows come from PCIS,
            which only tracks orders it routed natively — so the totals include failed attempts, and
            each courier's share is of DI volume, not of the whole fleet (which includes Clickpost).
            Without saying so, a courier's row here looks inconsistent with the same courier in the
            DI-volume card below, which counts successes only. */}
        <Panel
          title='Volume by courier'
          note='Placement attempts per courier, split success vs failed. Shares are of Direct Integration volume.'
        >
          {courierRows.length ? (
            <Stack spacing={1.25}>
              {courierRows.map((r) => (
                <SplitLabelledBar
                  key={r.key}
                  label={r.key}
                  sublabel={`${pct(r.failureRatePct, 1)} failed · ${pct(r.sharePct, 1)} of DI volume`}
                  value={nf(r.total)}
                  successPct={r.total ? ((r.total - r.failed) / maxCourier) * 100 : 0}
                  failPct={r.total ? (r.failed / maxCourier) * 100 : 0}
                />
              ))}
            </Stack>
          ) : (
            <Empty />
          )}
        </Panel>

        <Panel title='Needs attention' note={`Failure rate ≥${ATTENTION.failureRatePct}% or p95 ≥${ATTENTION.p95Ms / 1000}s, on couriers with ≥${ATTENTION.minVolume} orders`}>
          {attention.length ? (
            <Stack spacing={1.25}>
              {attention.map((a, i) => (
                <Callout key={i} tone={a.tone} icon={a.icon} title={a.title}>
                  {a.body}
                </Callout>
              ))}
              {ov.failedInvoiceValue && Number(ov.failedInvoiceValue) > 0 ? (
                <Callout tone={CORAL} icon='₹' title={`${money(ov.failedInvoiceValue)} of invoice value behind failed placements`}>
                  See the Failures tab for how much of it was recovered on retry.
                </Callout>
              ) : null}
            </Stack>
          ) : (
            <Empty>Nothing above threshold — every courier is inside its failure and latency budget.</Empty>
          )}
        </Panel>
      </Box>

      <Box sx={{mt: 2}}>
        <Panel
          title='Direct Integration vs Clickpost by courier'
          /* "Successfully placed" distinguishes these numbers from Volume by courier above, which
             counts placement ATTEMPTS — the same courier legitimately shows a lower figure here,
             and without the word the two cards look like they contradict each other. */
          note='Successfully placed orders per parent courier, split by how each was routed. Bar width is the courier’s share of the largest; the split within it is DI vs Clickpost.'
          action={routingRefreshing ? <Refreshing /> : null}
        >
          {routingByCourier.length ? (
            <Stack spacing={1.25}>
              {routingByCourier.map((p) => (
                /* Both segments are scaled against the LARGEST courier, not against this row's
                   own total. So the full bar length reads as that courier's share of volume,
                   while the teal/amber boundary inside it reads as its DI-vs-Clickpost mix —
                   one bar answering "how big" and "how migrated" at once. Scaling each row to
                   its own 100% would make every courier the same length and lose the first. */
                <SplitLabelledBar
                  key={p.name}
                  label={p.name}
                  sublabel={`${nf(p.nativeOrders)} DI · ${nf(p.clickpostOrders)} Clickpost`}
                  value={nf(p.totalOrders)}
                  successPct={(p.nativeOrders / routingByCourier[0].totalOrders) * 100}
                  failPct={(p.clickpostOrders / routingByCourier[0].totalOrders) * 100}
                  successPalette={TEAL}
                  failPalette={AMBER}
                />
              ))}
            </Stack>
          ) : routingRefreshing ? (
            /* Without this branch the still-loading panel reads "No orders", which is a confirmed
               zero rather than "not answered yet". */
            <TableSkeleton rows={4} />
          ) : splitError ? (
            <Empty>{splitError}</Empty>
          ) : (
            <Empty>No orders in this window.</Empty>
          )}
        </Panel>
      </Box>
    </>
  )
}

TabOverview.propTypes = {
  filters: PropTypes.object.isRequired,
  bucket: PropTypes.string,
  pollMs: PropTypes.number,
}
